import { Text } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { articlesActions } from '@/store/articles';
import { useLazyGetFacetsQuery } from '@/store/articles/services/articleSearch';
import SelectedFilters from 'components/Functional/ArticleFilterBar/SelectedFilters';
import ArticleFilters from 'components/Functional/ArticleFilterBar/ArticleFilters';
import MobileBaseFilters from 'components/Functional/Filters/MobileFilter/MobileBaseFilters';
import styles from './FilterBarFunctional.module.scss';
import useSharedFiltersLogic from 'hooks/useSharedFiltersLogic';
import { useSearchParams } from 'next/navigation';

type FilterMappingType = {
  articleBaseUrl: string;
  contentType: string;
  selectedFilter: string;
  filterBarSubHeading: string;
  site: string;
  filters: Array<{
    displayName: string;
    id: string;
  }>;
};

type Props = {
  e2eID: string;
  formattedMapping: FilterMappingType;
  entityTypes: string;
};

const setInitialFilters = (filters) => {
  return filters.map((filter) => ({
    label: filter.displayName,
    name: filter.id,
    id: filter.id,
    displayName: filter.displayName,
    displayNameKey: '',
    options: [],
  }));
};

const FilterBarFunctional = ({ formattedMapping, entityTypes, e2eID }: Props) => {
  const { sitecoreContext } = useSitecoreContext();
  const filterBarSubHeading = formattedMapping?.filterBarSubHeading;
  const dispatch = useDispatch();
  const applicableFilters = formattedMapping.filters.map((filter) => filter.id);
  const searchParams = useSearchParams();
  const {
    filterApply,
    filters,
    setFilters,
    allOptions,
    appliedFilters,
    appliedFilterOptions,
    selectedFilterOptions,
    onResetUnAppliedOptions,
    selectFilter,
    onClearFilters,
    getFiltersFromURL,
  } = useSharedFiltersLogic(setInitialFilters(formattedMapping.filters));

  const [fetchFacets, { data, isFetching }] = useLazyGetFacetsQuery();

  const facetsPayload = useMemo(() => {
    return {
      filters: appliedFilters,
      search: {
        sites: [formattedMapping.site],
        language: sitecoreContext.language,
        entityTypes: [entityTypes],
        contentTypes: (formattedMapping.contentType && [formattedMapping.contentType]) || null,
      },
      facets: formattedMapping.filters.map((filter) => filter.id),
    };
  }, [appliedFilters, formattedMapping, sitecoreContext.language, entityTypes]);

  const requestFacets = async (selectedFilterName) => {
    const { data } = await fetchFacets({
      ...facetsPayload,
      filters: facetsPayload.filters.filter((filter) => filter.name !== selectedFilterName),
    });
    const mergeFilters = (initialFilters, updatedFilters) => {
      if (!initialFilters || !updatedFilters) {
        console.error('No data found', {
          initialFilters,
          updatedFilters,
        });
        return filters;
      }
      return initialFilters.map((initialFilter) => {
        const updatedFilter = updatedFilters.find((f) => f.name === initialFilter.name);

        if (!updatedFilter) {
          return initialFilter; // Filter not returned in updated set
        }

        // Merge options
        const allOptionTexts = new Set([
          ...initialFilter.options.map((o) => o.text),
          ...updatedFilter.options.map((o) => o.text),
        ]);

        const mergedOptions = Array.from(allOptionTexts).map((text) => {
          const original = initialFilter?.options.find((o) => o.text === text);
          const updated = updatedFilter?.options.find((o) => o.text === text);

          return {
            text,
            count: updated?.count ?? updated?.count ?? undefined,
            selected: selectedFilterOptions.find(
              (filter) =>
                filter.id === updatedFilter.name &&
                filter.selectedOptions.find((option) => option.text === updated?.text)
            ),
            applied: !!appliedFilters.find((appliedFilter) =>
              appliedFilter.values.includes(updated?.text)
            ),
            disabled: !updated?.count,
            aggregateCount: updated?.count ?? undefined,
            showAggregateCount: original?.showAggregateCount ?? true,
          };
        });

        return {
          ...updatedFilter,
          label: initialFilter.label,
          id: initialFilter.name,
          displayName: initialFilter.displayName,
          displayNameKey: initialFilter.displayNameKey,
          options: mergedOptions || [],
        };
      });
    };

    setFilters(mergeFilters(filters, data));
  };

  useEffect(() => {
    const preselectedFilters = getFiltersFromURL(applicableFilters);
    if (preselectedFilters.length !== 0) {
      dispatch(articlesActions.setAppliedFilters(preselectedFilters));
    }
    dispatch(articlesActions.setContentType(formattedMapping.contentType));
    fetchFacets(facetsPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data?.length) {
      const filtersFormURL = getFiltersFromURL(applicableFilters);
      const updated = filters.map((filter) => {
        return {
          ...filter,
          options:
            data
              .find((item) => item.name === filter.name)
              ?.options.map((option) => {
                return {
                  ...option,
                  applied: !!filtersFormURL.find((filter) => {
                    return filter.values.some(
                      (filterOption) => filterOption.toLowerCase() === option.text.toLowerCase()
                    );
                  }),
                  selected: !!filtersFormURL.find((filter) => {
                    return filter.values.some(
                      (filterOption) => filterOption.toLowerCase() === option.text.toLowerCase()
                    );
                  }),
                };
              }) || [],
        };
      });
      setFilters(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchParams]);

  useEffect(() => {
    const preselectedFilters = getFiltersFromURL(applicableFilters);
    if (preselectedFilters.length !== 0) {
      dispatch(articlesActions.setAppliedFilters(preselectedFilters));
    }
  }, [searchParams]);

  if (!filters) return null;

  return (
    <div className="container pt-7 pb-5 background-white" id={e2eID}>
      <div className={`row ${styles['article-filter-bar-root']}`}>
        <div className="col-12">
          <div className="px-md-2 py-5 p-lg-5 background-digital-light-grey">
            <div className="d-flex align-items-start flex-column flex-md-row flex-lg-column">
              {filterBarSubHeading && (
                <Text tag="h4" field={{ value: filterBarSubHeading }} className="col-12 col-md-7" />
              )}

              {filters?.length !== 0 && (
                <>
                  <div className="col d-none d-lg-flex mt-4 mt-md-0">
                    <ArticleFilters
                      filters={filters}
                      loading={isFetching}
                      onSelect={selectFilter}
                      onClearFilters={(filter) => onClearFilters(filter, 'multipleOptions')}
                      filterApply={filterApply}
                      onResetUnAppliedOptions={onResetUnAppliedOptions}
                      getFacets={requestFacets}
                    />
                  </div>

                  <MobileBaseFilters
                    className="col-12 col-md-5 d-flex d-lg-none mt-4 mt-md-0 mt-lg-3"
                    buttonFullWidth
                    filters={filters}
                    isFetching={isFetching}
                    allOptions={allOptions}
                    showDatePicker={false}
                    needAggregateCount={requestFacets}
                    optionSelect={selectFilter}
                    resetUnAppliedOptions={onResetUnAppliedOptions}
                    filterApply={filterApply}
                    clearFilters={onClearFilters}
                    selectedOptions={selectedFilterOptions}
                  />
                </>
              )}

              {appliedFilters.length !== 0 && (
                <SelectedFilters
                  className="mt-4"
                  appliedFilterOptions={appliedFilterOptions}
                  onDeleteOption={(option) => onClearFilters(option, 'singleOption')}
                  onClearFilters={onClearFilters}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterBarFunctional;
